import type {
  ExerciseDetails,
  SessionDTO,
  Session,
  SessionRecap,
  SessionRecapDTO,
  SessionStatusResponse,
  SessionStatusResponseDTO,
  MusicSources,
  MusicSourcesDTO,
} from "@akina/domain";
import { toCamelCase } from "@akina/utils";
import { nullableDecodeHtmlTextFromString } from "@akina/utils/decodeHtmlTextFromString";
import type {
  ReactionEnum,
  SessionOverview,
  TreatmentFeedbackResponse,
  TreatmentFeedbackResponseApi,
} from "types/schemas";
import { type CMSAsset, type CMSVideoAndAudioData } from "../adapters";
import { musicSourcesMapper, sessionMapper, sessionRecapMapper, sessionStatusResponseMapper } from "../mappers";
import { type ApiConfiguration, ApiMethod } from "./shared";

export const therapySessionApi = (config: ApiConfiguration) => ({
  async getSessionFeedback(treatmentId: string, reviewedOnly?: boolean): Promise<SessionOverview[]> {
    const result = await config.httpService.fetch<TreatmentFeedbackResponseApi>({
      method: ApiMethod.GET,
      url: config.endpoints.therapySessions.feedback.get(treatmentId, reviewedOnly),
    });

    const response = toCamelCase<TreatmentFeedbackResponseApi, TreatmentFeedbackResponse>(result);

    const feedback: SessionOverview[] = (response.treatmentFeedback ?? []).map((session): SessionOverview => {
      const therapistFeedback = session.therapistFeedback
        ? {
            ...session.therapistFeedback,
            text: nullableDecodeHtmlTextFromString(session.therapistFeedback.text ?? undefined) ?? null,
          }
        : null;

      return {
        ...session,
        selfReflectionComment: nullableDecodeHtmlTextFromString(session.selfReflectionComment ?? undefined) ?? null,
        therapistFeedback,
        routineOverviews: session.routineOverviews.map(routine => ({
          ...routine,
          therapistFeedback: routine.therapistFeedback
            ? {
                ...routine.therapistFeedback,
                text: nullableDecodeHtmlTextFromString(routine.therapistFeedback.text ?? undefined) ?? null,
              }
            : null,
        })),
      };
    });

    return feedback.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  },
  async setFeedbackViewed(
    treatmentId: string,
    sessionId: string,
  ): Promise<{
    feedback: SessionOverview[];
    treatmentId: string;
    sessionId: string;
  }> {
    await config.httpService.fetch<void>({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapySessions.feedback.viewed,
      data: {
        treatment_id: treatmentId,
        session_log_id: sessionId,
      },
    });

    return {
      feedback: await this.getSessionFeedback(treatmentId),
      treatmentId,
      sessionId,
    };
  },
  async getSessionRecap(sessionId: string): Promise<SessionRecap | undefined> {
    const result = await config.httpService.fetch<SessionRecapDTO>({
      method: ApiMethod.GET,
      url: config.endpoints.therapySessions.sessionRecap(sessionId),
    });

    return sessionRecapMapper.toDomain(result);
  },
  async getSessionStatus(sessionId: string): Promise<SessionStatusResponse | undefined> {
    const result = await config.httpService.fetch<SessionStatusResponseDTO>({
      method: ApiMethod.GET,
      url: config.endpoints.therapySessions.sessionStatus(sessionId),
    });
    return sessionStatusResponseMapper.toDomain(result);
  },
  async getMusicSources(stationId?: string): Promise<MusicSources> {
    const response = await config.httpService.fetch<MusicSourcesDTO>({
      method: ApiMethod.GET,
      url: config.endpoints.therapySessions.musicSources(stationId ?? ""),
    });

    return musicSourcesMapper.toDomain(response);
  },
  async sendSessionFeedbackReaction(sessionId: string, reaction: ReactionEnum | null): Promise<void> {
    await config.httpService.fetch<void>({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapySessions.feedback.react,
      data: {
        session_log_id: sessionId,
        reaction,
      },
    });
  },
  async sendRoutineFeedbackReaction(
    sessionId: string,
    routineId: number,
    reaction: ReactionEnum | null,
  ): Promise<void> {
    await config.httpService.fetch<void>({
      method: ApiMethod.PATCH,
      url: config.endpoints.sessionReview.routineFeedback.react,
      data: {
        session_log_id: sessionId,
        routine_log_id: routineId,
        reaction,
      },
    });
  },
  async getNextSession(
    treatmentId: string,
    params: { includeMsps: boolean; onlyScriptGeneration: boolean },
  ): Promise<Session | undefined> {
    const result = await config.httpService.fetch<SessionDTO>({
      method: ApiMethod.GET,
      url: config.endpoints.therapySessions.nextSession(treatmentId, params),
    });

    return sessionMapper.toDomain(result);
  },
  async getImageByKey(key: string): Promise<string | undefined> {
    const response = await config.cmsService.get<{
      MediaURL: CMSAsset;
    }>(config.cmsEndpoints.image(key));

    const story = response.data.stories[0];

    if (!story) return undefined;

    return story.content.MediaURL?.filename;
  },

  async getExerciseDetails(key: string): Promise<ExerciseDetails | undefined> {
    const response = await config.cmsService.get<{
      DisplayedName: string;
      Description: string;
      Thumbnail: CMSAsset;
      PreviewGif: CMSAsset;
      InstructionTextBullets: { Text: string }[];
      MistakeVideos: CMSVideoAndAudioData[];
    }>(config.cmsEndpoints.exercise(key));

    const story = response.data.stories[0];

    if (!story) return undefined;

    return {
      key: story.slug,
      description: story.content.Description,
      name: story.content.DisplayedName,
      imageUrl: story.content.Thumbnail.filename,
      previewGif: story.content.PreviewGif?.filename,
      instructions: story.content.InstructionTextBullets.map((s: { Text: string }) => s.Text),
      firstInstructionVideoUrl: story.content.MistakeVideos?.[0]?.Video?.[0]?.media_url?.url,
    };
  },
  async startSession(sessionId: string): Promise<void> {
    await config.httpService.fetch<string>({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapySessions.startSession,
      data: { session_log_id: sessionId },
    });

    return;
  },
  async endSession(sessionId: string): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapySessions.endSession,
      data: { session_log_id: sessionId },
    });
  },
  async cancelSession(sessionId: string): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapySessions.cancelSession,
      data: { session_log_id: sessionId },
    });
  },
  async cancelRoutine({
    routineId,
    sessionId,
    totalCompletedSetBlocks,
    exerciseTemplateKey,
    routineMetric,
  }: {
    routineId: number;
    sessionId: string;
    totalCompletedSetBlocks: number;
    exerciseTemplateKey: string;
    routineMetric: string;
  }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapySessions.cancelRoutine,
      data: {
        routine_log_id: routineId,
        session_log_id: sessionId,
        number_of_set_blocks_completed: totalCompletedSetBlocks,
        exercise_template_key: exerciseTemplateKey,
        routine_metric: routineMetric,
      },
    });
  },
  async updateRoutineProgress({
    routineId,
    sessionId,
    routineMetric,
    totalCompletedSetBlocks,
    exerciseTemplateKey,
  }: {
    routineId: number;
    sessionId: string;
    routineMetric: string;
    totalCompletedSetBlocks: number;
    exerciseTemplateKey: string;
  }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapySessions.updateRoutineProgress,
      data: {
        routine_log_id: routineId,
        session_log_id: sessionId,
        routine_metric: routineMetric,
        number_of_set_blocks_completed: totalCompletedSetBlocks,
        exercise_template_key: exerciseTemplateKey,
      },
    });
  },
  async updateSelfReflection({
    sessionId,
    painLevel,
    painfulExerciseIds,
    difficultExerciseIds,
    feedback,
    wasDifficult,
  }: {
    sessionId: string;
    painLevel: number;
    painfulExerciseIds: string[];
    difficultExerciseIds: string[];
    feedback: string | undefined;
    wasDifficult: boolean;
  }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapySessions.updateSelfReflection,
      data: {
        session_log_id: sessionId,
        pain_level: painLevel,
        painful_exercises: painfulExerciseIds,
        difficult_exercises: difficultExerciseIds,
        comments: feedback,
        was_difficult: wasDifficult,
      },
    });
  },
});
